// src/pages/msp-inventory/pages/reports/ReportsHubPage.jsx
import React, { useState } from "react";
import { BarChart3, AlertTriangle, AlertCircle, DollarSign, Percent, Truck, Calendar, Package, ChevronLeft } from "lucide-react";
import ValuationReportPage from "./ValuationReportPage";
import RestockAlertsPage from "./RestockAlertsPage";
import SfNotZeroedPage from "./SfNotZeroedPage";
import SkuDetailPage from "./SkuDetailPage";
import PnLReportPage from "./PnLReportPage";
import SalesTaxReportPage from "./SalesTaxReportPage";
import SupplierReportPage from "./SupplierReportPage";
import ExpirationsReportPage from "./ExpirationsReportPage";
import StorageReportPage from "./StorageReportPage";
import LoadingGame from "../../components/reports/LoadingGame";
import MonthEndValuationReminder from "../../components/reports/MonthEndValuationReminder";

const REPORTS = [
    { id: "valuation", title: "Inventory Valuation", desc: "SF / WFS / Inbound stock value, by flag", icon: BarChart3, color: "from-teal-500 to-blue-600", ready: true },
    { id: "restock", title: "Restock Alerts", desc: "Fast-sellers projected to run out soon", icon: AlertTriangle, color: "from-orange-400 to-red-500", ready: true },
    { id: "sf-not-zeroed", title: "SF Not Zeroed", desc: "WFS items still carrying SF stock", icon: AlertCircle, color: "from-red-400 to-pink-500", ready: true },
    { id: "pnl", title: "P&L Report", desc: "Revenue, COGS, and margin over time", icon: DollarSign, color: "from-green-500 to-teal-600", ready: true },
    { id: "sales-tax", title: "Sales Tax", desc: "Tax collected, by state", icon: Percent, color: "from-purple-500 to-indigo-600", ready: true },
    { id: "supplier", title: "Supplier Report", desc: "Performance broken down by supplier", icon: Truck, color: "from-blue-500 to-cyan-600", ready: true },
    { id: "expirations", title: "Expiring Inventory", desc: "Items approaching their expiration date", icon: Calendar, color: "from-yellow-400 to-orange-500", ready: true },
    { id: "storage", title: "Storage Report", desc: "Items approaching 365 days in storage - prevent high fees!", icon: Package, color: "from-slate-500 to-gray-700", ready: true },
];

const ReportsHubPage = ({ itemSyncStatus, inventorySyncStatus, orderSyncStatus }) => {
    // A single "is ANYTHING syncing right now" flag, so every report page
    // can show the same loading game while data underneath it might still
    // be settling — regardless of which specific sync (catalog, inventory,
    // orders) is the one currently running.
    const anySyncRunning = !!(itemSyncStatus?.running || inventorySyncStatus?.running || orderSyncStatus?.running);
    const [view, setView] = useState("hub"); // hub | <report id> | sku-detail
    const [selectedSkuId, setSelectedSkuId] = useState(null);
    const [returnTo, setReturnTo] = useState("hub");

    const openSku = (catalogItemId) => {
        setReturnTo(view);
        setSelectedSkuId(catalogItemId);
        setView("sku-detail");
    };

    if (view === "sku-detail") {
        return <SkuDetailPage catalogItemId={selectedSkuId} onBack={() => setView(returnTo)} />;
    }

    if (view !== "hub") {
        const report = REPORTS.find((r) => r.id === view);
        return (
            <div>
                <div className="max-w-6xl mx-auto pt-6 px-6">
                    <button onClick={() => setView("hub")} className="flex items-center gap-1.5 text-gray-400 hover:text-gray-600 text-sm font-semibold mb-2">
                        <ChevronLeft size={16} /> All Reports
                    </button>
                </div>
                {anySyncRunning ? (
                    <LoadingGame label="Syncing your data — hang tight..." />
                ) : (
                    <>
                        {view === "valuation" && <ValuationReportPage onOpenSku={openSku} inventorySyncStatus={inventorySyncStatus} />}
                        {view === "restock" && <RestockAlertsPage onOpenSku={openSku} />}
                        {view === "sf-not-zeroed" && <SfNotZeroedPage onOpenSku={openSku} />}
                        {view === "pnl" && <PnLReportPage onOpenSku={openSku} />}
                        {view === "sales-tax" && <SalesTaxReportPage />}
                        {view === "supplier" && <SupplierReportPage onOpenSku={openSku} />}
                        {view === "expirations" && <ExpirationsReportPage onOpenSku={openSku} />}
                        {view === "storage" && <StorageReportPage onOpenSku={openSku} />}
                        {!report?.ready && (
                            <div className="max-w-4xl mx-auto py-16 text-center text-gray-400">Coming very soon.</div>
                        )}
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto py-8 px-6">
            <MonthEndValuationReminder wrapperClassName="mb-6" hint="Open Inventory Valuation below and use its Download button." />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Reports</h1>
            <p className="text-gray-500 text-sm mb-6">Full financial and operational visibility for your Walmart business.</p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {REPORTS.map((r) => (
                    <button
                        key={r.id}
                        onClick={() => r.ready && setView(r.id)}
                        disabled={!r.ready}
                        className={`bg-white rounded-2xl shadow-lg p-5 text-left border-2 border-transparent transition ${r.ready ? "hover:border-teal-400 hover:shadow-xl" : "opacity-50 cursor-not-allowed"}`}
                    >
                        <div className={`w-11 h-11 bg-gradient-to-br ${r.color} rounded-xl flex items-center justify-center mb-3 shadow`}>
                            <r.icon size={20} className="text-white" />
                        </div>
                        <h3 className="font-bold text-gray-800 text-sm mb-1">{r.title}</h3>
                        <p className="text-xs text-gray-500 leading-relaxed">{r.desc}</p>
                        {!r.ready && <span className="inline-block mt-2 text-[10px] bg-gray-100 text-gray-400 font-bold px-2 py-0.5 rounded-full">Coming Soon</span>}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default ReportsHubPage;